import React, { useEffect, useRef, useState } from "react";
import Sidebar from "../components/Sidebar";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Box, Menu, MenuItem, Popover, SpeedDial, SpeedDialAction, SpeedDialIcon, Typography } from "@mui/material";
import { BookOpenText, GraduationCap, MessageCircle, Palette, Rotate3D, RotateCcw } from "lucide-react";
import { useSetRecoilState } from "recoil";
import { themeModeAtom } from "../recoil/settings";
import { ThemeName } from "../theme/themes";
import ManualModal from "../components/new/ManualModal";
import ChatbotPanel from "../components/new/ChatbotPanel";
import { useSWCheck } from "../hooks/useSWcheck";
import SWCertModal from "../components/SWCertModal";
import BrandHeader from "../components/BrandTitle";
import { restartServer } from "../services/serverService";
import { useTutorial, TutorialKey } from "../context/TutorialContext";
import { TUTORIAL_OPTIONS, resolveTutorialPath } from "../tutorial/options";

type ThemeOption = {
  key: ThemeName; // 실제 setTheme에 쓰는 값
  label: string; // 화면 표시용
  bgColor: string; // 선택 UI 배경색 (고정)
  textColor?: string;
};

export const themeOptions: ThemeOption[] = [
  {
    key: "light",
    label: "Light",
    bgColor: "#f8f9fa",
    textColor: "#2563eb",
  },
  {
    key: "nature",
    label: "Nature",
    bgColor: "#EEF2EF",
    textColor: "#3E5B52",
  },
  {
    key: "pastel",
    label: "Pastel",
    bgColor: "#e5f0faff",
    textColor: "#6B69A8",
  },
  {
    key: "vintage",
    label: "Vintage",
    bgColor: "#FBFAF6",
    textColor: "#6B8E6E",
  },
  {
    key: "nordic",
    label: "Nordic",
    bgColor: "#ECF7F5",
    textColor: "#004E64",
  },
  {
    key: "dark",
    label: "Dark",
    bgColor: "#0F172A",
    textColor: "#E5E7EB",
  },
  {
    key: "nordicDark",
    label: "Nordic Dark",
    bgColor: "#001A22",
    textColor: "#8EDBD2",
  },
  {
    key: "neoDark",
    label: "Neo Dark",
    bgColor: "#111827",
    textColor: "#A5B4FC",
  },
];

export default function Layout() {
  const location = useLocation();
  const [activeMenu, setActiveMenu] = useState(() => {
    if (location.pathname.includes("projects")) return "projects";
    if (location.pathname.includes("dashboard")) return "dashboard";
    return "dashboard";
  });
  const [themeAnchor, setThemeAnchor] = useState<HTMLElement | null>(null);
  const [helpAnchor, setHelpAnchor] = useState<HTMLElement | null>(null);
  const openTheme = Boolean(themeAnchor);
  const openHelp = Boolean(helpAnchor);
  const setThemeMode = useSetRecoilState(themeModeAtom);
  const dialRef = useRef<HTMLDivElement>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const swStatus = useSWCheck();
  const [swModalOpen, setSwModalOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const mode = localStorage.getItem("appModeOverride") ?? process.env.APP_MODE ?? "default";

  const BRAND_BY_MODE: Record<string, { title: string; badge?: string }> = {
    default: { title: "HAIF", badge: "PRO" },
    dataCA: { title: "Data CA" },
  };
  const brand = BRAND_BY_MODE[mode] ?? BRAND_BY_MODE.default;

  const pathToMenu: Record<string, string> = {
    "/main/dashboard": "dashboard",
    "/main/notices": "notices",
    "/main/dataset": "dataset",
    "/main/projects": "projects",
    "/main/inspection": "inspection",
    "/main/training": "training",
  };

  const navigate = useNavigate();
  const { startTourWithDelay } = useTutorial();

  const handleTutorialSelect = async (key: TutorialKey, path: string) => {
    setTutorialOpen(false);
    const resolved = await resolveTutorialPath(key, path);
    if (resolved === null) return;
    startTourWithDelay(key, navigate, resolved, 2000);
  };

  const handleMouseEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };

  const handleMouseLeave = () => {
    if (pinned) return;
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  const handleClick = () => {
    setPinned((prev) => {
      if (prev) {
        setOpen(false);
        return false;
      }
      setOpen(true);
      return true;
    });
  };

  useEffect(() => {
    if (!pinned) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setPinned(false);
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [pinned]);

  useEffect(() => {
    const found = Object.entries(pathToMenu).find(([path]) => location.pathname.startsWith(path));
    if (found) {
      setActiveMenu(found[1]);
    }
  }, [location.pathname]);

  // 액션 버튼은 다이얼이 닫히면 사라지므로 다이얼 자체에 앵커
  const handleOpenTheme = () => {
    setThemeAnchor(dialRef.current);
  };

  const handleCloseTheme = () => {
    setThemeAnchor(null);
  };

  const handleHelp = () => {
    setHelpAnchor(dialRef.current);
  };

  const handleCloseHelp = () => {
    setHelpAnchor(null);
  };

  useEffect(() => {
    if (swStatus === "not_supported" || swStatus === "not_registered" || swStatus === "no_controller") {
      const dismissed = sessionStorage.getItem("sw-cert-dismissed");
      if (!dismissed) setSwModalOpen(true);
    }
  }, [swStatus]);

  const handleSwModalClose = () => {
    setSwModalOpen(false);
    sessionStorage.setItem("sw-cert-dismissed", "1");
  };

  return (
    <div
      className="min-h-screen relative"
      style={{
        backgroundColor: "var(--app-bg)",
        color: "var(--app-text)",
      }}
    >
      <SWCertModal open={swModalOpen} onClose={handleSwModalClose} />
      <div
        ref={containerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ position: "fixed", top: 16, left: 16, zIndex: 9999 }}
      >
        <BrandHeader title={brand.title} badge={brand.badge} activeMenu={activeMenu} setActiveMenu={setActiveMenu} />{" "}
        {open && <Sidebar activeMenu={activeMenu} setActiveMenu={setActiveMenu} pinned={pinned} />}
      </div>
      <div
        className="ml-32 min-h-screen pr-24 pl-24"
        style={{
          backgroundColor: "var(--page-bg)",
          color: "var(--page-text)",
        }}
      >
        <Outlet />
      </div>

      <SpeedDial
        ref={dialRef}
        ariaLabel="quick menu"
        icon={<SpeedDialIcon />}
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 1300,
          "& .MuiSpeedDial-fab": {
            background: "linear-gradient(135deg, var(--brand-gradient-from), var(--brand-gradient-to))",
            boxShadow: "var(--brand-shadow)",
          },
        }}
      >
        {/* <SpeedDialAction icon={<Palette size={20} />} tooltipTitle="테마 변경" onClick={handleOpenTheme} /> */}
        <SpeedDialAction icon={<BookOpenText size={20} />} tooltipTitle="매뉴얼" onClick={handleHelp} />
        <SpeedDialAction
          icon={<GraduationCap size={20} />}
          tooltipTitle="튜토리얼"
          onClick={() => setTutorialOpen(true)}
        />
        {/* <SpeedDialAction
          icon={<MessageCircle size={20} />}
          tooltipTitle="챗봇 문의"
          onClick={() => setChatOpen(true)}
        /> */}
        <SpeedDialAction
          icon={<RotateCcw size={20} />}
          tooltipTitle="재시작"
          onClick={async () => await restartServer()}
        />
      </SpeedDial>
      <Popover
        open={openTheme}
        anchorEl={themeAnchor}
        onClose={handleCloseTheme}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Box sx={{ p: 2, display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 1.5, minWidth: 200 }}>
          {themeOptions.map((option) => (
            <Box
              key={option.key}
              onClick={() => {
                setThemeMode(option.key);
                handleCloseTheme();
              }}
              sx={{
                cursor: "pointer",
                p: 1.5,
                borderRadius: "10px",
                textAlign: "center",
                backgroundColor: option.bgColor,
                color: option.textColor ?? "#111827",
                transition: "all 0.15s ease",
                "&:hover": { transform: "translateY(-1px)", filter: "brightness(0.96)" },
              }}
            >
              <Typography fontWeight={600}>{option.label}</Typography>
            </Box>
          ))}
        </Box>
      </Popover>
      <Menu
        anchorEl={dialRef.current}
        open={tutorialOpen}
        onClose={() => setTutorialOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        slotProps={{ paper: { sx: { minWidth: 200, borderRadius: "12px", p: 0.75 } } }}
      >
        {TUTORIAL_OPTIONS.map(({ key, label, path }) => (
          <MenuItem
            key={key}
            onClick={() => handleTutorialSelect(key, path)}
            sx={{ fontSize: 13, fontWeight: 500, borderRadius: "7px", py: 0.85, minHeight: "auto" }}
          >
            {label}
          </MenuItem>
        ))}
      </Menu>
      <ChatbotPanel open={chatOpen} onClose={() => setChatOpen(false)} />
      <ManualModal open={openHelp} onClose={handleCloseHelp} />
    </div>
  );
}
